import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import FetchTranscriptSpeakers from '@Application/Beneficiaries/Transcripts/Queries/FetchTranscriptSpeakers';
import FetchTranscriptTurns from '@Application/Beneficiaries/Transcripts/Queries/FetchTranscriptTurns';
import fetchTranscriptSpeakers from '@Application/Beneficiaries/Transcripts/fetchTranscriptSpeakers';
import fetchTranscriptTurns from '@Application/Beneficiaries/Transcripts/fetchTranscriptTurns';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import TranscriptPlayerBar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/TranscriptPlayerBar/TranscriptPlayerBar';
import TranscriptReviewToolbar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Toolbar/TranscriptReviewToolbar';
import type InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import type TranscriptView from '@UI/Store/Interviews/Views/TranscriptView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import AudioPlayerProvider from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Providers/AudioPlayerProvider';
import TranscriptTurnsReviewerContainer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/TranscriptTurnsReviewer/TranscriptTurnsReviewerContainer';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import TurnRevisionProvider from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Providers/TurnRevisionProvider';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	padding: 1.5rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	/** Loaded upstream (T9) — this section never fetches its own transcript. */
	readonly transcript: TranscriptView;
	readonly recordingStatus: 'stored' | 'uploading' | null;
	readonly recordingId: number | null;
	/** `false` for an entered text, which has no audio to play. */
	readonly showsAudio: boolean;
	/** Transportés, jamais affichés : ce bloc écrit le signal du panneau d'attribution, qui les classe. */
	readonly participants: readonly InternalInterviewParticipantView[];
	/** `false` hides every write action (turn edit, speaker attribution, review); export and audio stay. */
	readonly isWritable: boolean;
};

/**
 * The transcript review block (§9.4): player bar, toolbar and the loaded turns. Owns two
 * `useStoreQuery` calls (turns page 0, speakers) and drives every page beyond the first with a plain
 * `dispatch`, since `useStoreQuery`'s own `stopOn` only ever asks for the page it is configured with
 * (`state-stores.md`). `runGuarded` centralises EF-48's confirmation: opening a different turn,
 * attributing a speaker and opening the export dialog all run through it, since all three could
 * otherwise discard a correction the user has not saved yet.
 */
const TranscriptReviewSection: React.FC<Props> = ({
	subject,
	interviewId,
	transcript,
	recordingStatus,
	recordingId,
	showsAudio,
	participants,
	isWritable,
}) => {
	const dispatch = useDispatch();
	const [isLoadingMore, setIsLoadingMore] = React.useState(false);
	const [hasNextPageError, setHasNextPageError] = React.useState(false);
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const {
		data: turns,
		error: turnsError,
		refetch: refetchTurns,
	} = useStoreQuery({
		selector: (state) => state.interviewsStore.transcriptTurns,
		stopOn: (value) => value !== null && value.interviewId === interviewId,
		query: () =>
			dispatch(
				fetchTranscriptTurns(new FetchTranscriptTurns(facilityId, subject, interviewId, 0, 200)),
			),
		deps: [facilityId, subject, interviewId],
	});

	const { data: speakers } = useStoreQuery({
		selector: (state) => state.interviewsStore.transcriptSpeakers,
		stopOn: (value) => value !== null && value.interviewId === interviewId,
		query: () =>
			dispatch(fetchTranscriptSpeakers(new FetchTranscriptSpeakers(facilityId, subject, interviewId))),
		deps: [facilityId, subject, interviewId],
	});

	const hasSpeakers = React.useMemo(() => speakers !== null && speakers.list.length > 0, [speakers]);

	// The footer's own guard for page N > 0: `turnsError` above only ever reflects the page 0 request
	// `useStoreQuery` itself issued (`state-stores.md`), never this one.
	const loadMore = React.useCallback((): void => {
		if (turns === null || !turns.hasMore || isLoadingMore) {
			return;
		}

		setIsLoadingMore(true);
		setHasNextPageError(false);

		dispatch(
			fetchTranscriptTurns(
				new FetchTranscriptTurns(facilityId, subject, interviewId, turns.loadedPageCount, 200),
			),
		)
			.catch(() => setHasNextPageError(true)) // toast shown by InterviewsNotifications either way
			.finally(() => setIsLoadingMore(false));
	}, [turns, isLoadingMore, dispatch, facilityId, subject, interviewId]);

	return (
		<TurnRevisionProvider interviewId={interviewId} subject={subject}>
			<AudioPlayerProvider
				subject={subject}
				interviewId={interviewId}
				recordingStatus={recordingStatus}
				recordingId={recordingId}
				turns={turns}
				isLoadingNextPage={isLoadingMore}
				loadNextPage={loadMore}
			>
				<section css={sectionStyle}>
					<ConditionalLoader data={showsAudio}>
						<TranscriptPlayerBar hasSpeakers={hasSpeakers} />
					</ConditionalLoader>
					<TranscriptReviewToolbar
						transcript={transcript}
						subject={subject}
						interviewId={interviewId}
						speakers={speakers}
						participants={participants}
						isWritable={isWritable}
					/>
					<TranscriptTurnsReviewerContainer
						turns={turns}
						turnsFetchError={turnsError}
						refetchTurns={refetchTurns}
						speakers={speakers}
						participants={participants}
						subject={subject}
						interviewId={interviewId}
						loadMoreTurns={loadMore}
						isLoadingMoreTurns={isLoadingMore}
						hasNextPageError={hasNextPageError}
					/>
				</section>
			</AudioPlayerProvider>
		</TurnRevisionProvider>
	);
};

export default TranscriptReviewSection;
